import { useEffect, useRef, useState } from "react";

interface DeleteConfirmDialogProps {
  candidateName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteConfirmDialog({ candidateName, onConfirm, onCancel }: DeleteConfirmDialogProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel]);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === backdropRef.current) onCancel(); }}
    >
      <div className="mx-4 w-full max-w-md rounded-card bg-white p-6 shadow-card">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100">
            <svg className="h-5 w-5 text-error-text" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[18px] font-semibold text-near-black">Delete candidate</h3>
            <p className="mt-0.5 text-sm text-text-secondary">This action cannot be undone.</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-text-focused">
          Are you sure you want to permanently delete{" "}
          <span className="font-semibold text-near-black">{candidateName}</span>?
          All screening data, scores, and breakdown will be lost.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-btn bg-surface-secondary px-5 py-2 text-sm font-medium text-text-focused transition-colors hover:bg-border-card"
          >
            Cancel
          </button>
          <button
            disabled={deleting}
            onClick={handleConfirm}
            className="rounded-btn bg-red-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:bg-border-card disabled:text-text-secondary"
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
